"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

interface ProjectDetailClientProps {
  project: {
    name: string;
    description: string;
    industry: string;
    links: Array<{ label: string; href: string }>;
    services: string[];
    process: {
      title: string;
      content: string;
    };
    images: string[];
    brandColors?: string[];
    testimonial: {
      author: string;
      text: string;
    };
    outcome: string;
  };
  slug: string;
}

const order = ["yapmarket", "pp-editor", "astra-finance", "echo-ai"];
const projectNames: Record<string, string> = {
  "yapmarket": "Yap.market",
  "pp-editor": "PP Editor",
  "astra-finance": "Astra Finance",
  "echo-ai": "Echo AI"
};

export default function ProjectDetailClient({ project, slug }: ProjectDetailClientProps) {
  const currentIndex = order.indexOf(slug);
  const nextIndex = currentIndex !== -1 ? (currentIndex + 1) % order.length : 0;
  const nextSlug = order[nextIndex];
  const nextName = projectNames[nextSlug];

  return (
    <main className="bg-white text-black min-h-screen font-sans overflow-x-hidden">
      {/* Navigation - Back Button */}
      <nav className="p-8 md:p-12">
        <Link href="/work" className="group inline-flex items-center gap-4">
          <div className="w-12 h-12 rounded-full border border-black/10 flex items-center justify-center group-hover:bg-black group-hover:border-black transition-all duration-500">
            <svg className="w-6 h-6 group-hover:text-white transition-colors rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
          <span className="font-display text-2xl uppercase tracking-tighter">Back to Work</span>
        </Link>
      </nav>

      <div className="max-w-[1400px] mx-auto px-8 md:px-12 space-y-32 pb-40">
        {/* Hero Section */}
        <section className="space-y-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="font-display text-7xl md:text-[12rem] uppercase leading-[0.8] tracking-tighter">
              {project.name}
            </h1>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 md:grid-cols-12 gap-12 pt-16 border-t border-black/10"
          >
            <div className="md:col-span-8">
              <p className="text-3xl md:text-4xl font-sans leading-tight text-black/80">
                {project.description}
              </p>
            </div>
            <div className="md:col-span-4 space-y-12">
              <div className="space-y-2">
                <h4 className="font-sans text-sm uppercase tracking-widest text-black/40">Industry</h4>
                <p className="text-xl uppercase font-sans">{project.industry}</p>
              </div>
              <div className="space-y-2">
                <h4 className="font-sans text-sm uppercase tracking-widest text-black/40">Links</h4>
                {project.links.map((link) => (
                  <a key={link.label} href={link.href} className="block text-xl uppercase font-sans hover:text-primary transition-colors">
                    {link.label}
                  </a>
                ))}
              </div>
              <div className="space-y-2">
                <h4 className="font-sans text-sm uppercase tracking-widest text-black/40">Services</h4>
                <div className="flex flex-wrap gap-2">
                  {project.services.map((service) => (
                    <span key={service} className="text-sm uppercase font-sans border border-black/10 px-3 py-1 rounded-full text-black/60">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Process Section */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-12 py-32">
          <div className="md:col-span-5">
            <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-none">
              {project.process.title}
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-xl md:text-2xl text-black/60 leading-relaxed">
              {project.process.content}
            </p>
          </div>
        </section>

        {/* Image Gallery */}
        {project.images && project.images.length > 0 && (
          <section className="space-y-24 md:space-y-32">
            {/* 1. Large Main Mockup */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-sm bg-black/[0.02]"
            >
              <Image 
                src={project.images[0]} 
                alt={`${project.name} Main View`} 
                fill 
                className="object-cover" 
                sizes="(max-width: 768px) 100vw, 1400px"
                priority 
              />
            </motion.div>

            {/* 2. Split Row: Gallery Image & Brand Identity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24">
              <motion.div 
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative aspect-square overflow-hidden rounded-sm bg-black/[0.02]"
              >
                {project.images[1] ? (
                  <Image 
                    src={project.images[1]} 
                    alt={`${project.name} Context View`} 
                    fill 
                    className="object-contain"
                    sizes="(max-width: 768px) 100vw, 700px"
                  />
                ) : (
                  <div className="w-full h-full bg-slate-50 flex items-center justify-center text-black/20 font-mono">NO_IMG_02</div>
                )}
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex flex-col gap-6 h-full justify-between"
              >
                {project.brandColors && project.brandColors.length > 0 ? (
                  project.brandColors.map((color, i) => (
                    <div 
                      key={color} 
                      className="flex-1 rounded-sm flex items-center px-12 relative overflow-hidden group/color min-h-[120px]" 
                      style={{ backgroundColor: color }}
                    >
                      <div className="flex items-baseline gap-6">
                        <span className="font-display text-6xl md:text-7xl uppercase tracking-tighter" style={{ color: i === 0 || color === "#FFFFFF" ? 'black' : 'white' }}>
                          Brand
                        </span>
                        <span className="font-sans text-sm uppercase tracking-[0.3em] opacity-40" style={{ color: i === 0 || color === "#FFFFFF" ? 'black' : 'white' }}>
                          {color}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex-1 rounded-sm bg-black flex items-center px-12 min-h-[120px]">
                    <span className="font-display text-6xl text-white uppercase tracking-tighter">Resilient</span>
                  </div>
                )}
              </motion.div>
            </div>

            {/* 3. Large Secondary Mockup */}
            {project.images[2] && (
              <motion.div 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-sm bg-black/[0.02]"
              >
                <Image 
                  src={project.images[2]} 
                  alt={`${project.name} Detail View`} 
                  fill 
                  className="object-cover" 
                  sizes="(max-width: 768px) 100vw, 1400px"
                />
              </motion.div>
            )}
          </section>
        )}

        {/* Testimonial Section */}
        <section className="py-32 border-t border-black/10">
          <div className="max-w-4xl space-y-12">
            <p className="font-sans text-3xl md:text-5xl leading-tight text-black italic">
              &ldquo;{project.testimonial.text}&rdquo;
            </p>
            <div className="space-y-1">
              <p className="font-display text-2xl uppercase tracking-tighter">{project.testimonial.author}</p>
              <p className="font-sans text-sm uppercase tracking-widest text-black/40">Testimonial</p>
            </div>
          </div>
        </section>

        {/* Next Work Section */}
        <section className="pt-32 border-t border-black/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-12">
            <div className="space-y-4 text-center md:text-left">
              <h2 className="font-display text-4xl uppercase tracking-tighter text-black/40 underline underline-offset-8">Next Project</h2>
              <p className="font-display text-6xl md:text-8xl uppercase tracking-tighter leading-none">
                {nextName}
              </p>
            </div>
            <Link 
              href={`/viewwork/${nextSlug}`}
              className="group w-40 h-40 rounded-full border-2 border-black flex items-center justify-center hover:bg-black transition-all duration-500"
            >
              <svg className="w-12 h-12 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>
      </div>

      {/* Background noise texture */}
      <div className="fixed inset-0 z-50 pointer-events-none opacity-[0.03] bg-noise" />
    </main>
  );
}
